import type { MosooThreadEvent, MosooThreadFile } from '../api/client';
import type { AgentTimelineEvent, TimelineEventMeta } from './normalize';

const AGENT_META: TimelineEventMeta = {
  lane: 'chat',
  namespace: 'mosoo',
  origin: 'agent',
  protocol: 'driver',
  visibility: 'chat',
};

const USER_META: TimelineEventMeta = {
  lane: 'chat',
  namespace: 'mosoo',
  origin: 'user',
  protocol: 'driver',
  visibility: 'chat',
};

function occurredAtMs(value: string): number {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Date.now();
}

export function normalizeMosooThreadEvent(
  event: MosooThreadEvent,
  agentId: string,
  agent = 'PitchPilot',
): AgentTimelineEvent[] {
  const base = {
    agent,
    agentId,
    rowId: `mosoo:${event.id}`,
    ts: occurredAtMs(event.occurredAt),
    turnId: event.runId ?? '',
  };

  switch (event.type) {
    case 'user.message':
      return [{
        content: event.content,
        messageId: event.id,
        meta: USER_META,
        rowId: base.rowId,
        ts: base.ts,
        turnId: base.turnId,
        type: 'message.user',
      }];
    case 'agent.message.delta':
      return event.content ? [{ ...base, delta: event.content, meta: AGENT_META, type: 'message.assistant.delta' }] : [];
    case 'agent.thinking.delta':
      return event.content ? [{ ...base, content: event.content, meta: AGENT_META, phase: 'thinking', source: 'driver', tool: 'thinking', type: 'action' }] : [];
    case 'run.started':
      return [{ ...base, content: 'The agent started this run.', meta: AGENT_META, phase: 'started', source: 'driver', tool: 'run', type: 'action' }];
    case 'tool.confirmation.required':
    case 'tool.use.started':
      return [{ ...base, content: event.content, meta: AGENT_META, phase: 'tool_use', source: 'driver', tool: event.content || 'tool', type: 'action' }];
    case 'tool.use.completed':
      return [{ ...base, content: event.content, meta: AGENT_META, phase: 'tool_result', source: 'driver', tool: event.content || 'tool', type: 'action' }];
    case 'file.changed':
      return [{ ...base, content: event.content, meta: AGENT_META, phase: 'writing', source: 'driver', tool: 'file', type: 'action' }];
    case 'run.completed':
      return [{ ...base, durationMs: event.durationMs ?? 0, meta: AGENT_META, summary: 'Completed', type: 'turn.completed' }];
    case 'run.failed':
      return [
        { ...base, message: event.content || 'Mosoo run failed.', meta: AGENT_META, type: 'error' },
        { ...base, durationMs: event.durationMs ?? 0, meta: AGENT_META, summary: 'Failed', type: 'turn.completed' },
      ];
    default:
      return [];
  }
}

export function normalizeMosooThreadFile(
  file: MosooThreadFile,
  agentId: string,
  fileUrl: string,
  agent = 'PitchPilot',
): AgentTimelineEvent | null {
  if (file.kind !== 'artifact' || !file.committed) return null;
  return {
    agent,
    agentId,
    fileUrl,
    isImage: file.mimeType?.startsWith('image/') === true,
    meta: AGENT_META,
    mime: file.mimeType ?? undefined,
    name: file.name,
    rowId: `mosoo-file:${file.id}`,
    size: file.size,
    ts: occurredAtMs(file.createdAt),
    turnId: '',
    type: 'message.assistant.file',
  };
}
